import { useTranslation } from "react-i18next";
import {
  AsyncCallArgs,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  InputFormField,
  isNotEmpty,
  SelectFormField,
  TextareaFormField,
  useAsyncCall,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { ViewDialog, CircularLoader } from "@duosoftbg/nacid-components";
import {
  createGlobalMessage,
  getGlobalMessage,
  getGlobalMessageTypes,
  updateGlobalMessage,
} from "../../../../../../axios/api/services";
import { FormProvider } from "react-hook-form";
import React, { useEffect, useState } from "react";
import useAppSelector from "../../../../../../hooks/redux/base/useAppSelector";
import useAppDispatch from "../../../../../../hooks/redux/base/useAppDispatch";
import { Alert } from "@mui/material";
import { closeModal } from "../../../../../../store/redux/slice/ComponentsControl/globalMessageControl";
import { GlobalMessageSaveDetails } from "../../../../../../types/globalMessageTypes";
import { globalMessageSaveInitialValues } from "../../../../../../init/globalMessageSaveInitialValues";
import { createGlobalMessageSaveValidationSchema } from "../../../../../../yup/schema/globalMessage/globalMessageSaveValidationSchema";
import { toast } from "react-toastify";

const Content = ({ loading, error, methods }) => {
  const { t } = useTranslation();
  const [globalMessageTypeMap, setGlobalMessageTypeMap] = useState([]);
  const { asyncCall } = useAsyncCall();

  useEffect(() => {
    const asyncCallArgs: AsyncCallArgs = {
      promise: getGlobalMessageTypes(),
      onSuccess: (response) => {
        const options = response.map((e) => {
          return { value: e.id, text: e.id };
        });
        setGlobalMessageTypeMap(options);
      },
      onError: () => {
        setGlobalMessageTypeMap(null);
      },
    };
    asyncCall(asyncCallArgs);
  }, [asyncCall]);

  if (loading) {
    return <CircularLoader />;
  }

  if (error) {
    return <Alert severity="error">{t("m.error.serverFetchingError")}</Alert>;
  }
  return (
    <FormProvider {...methods}>
      <form>
        <GridContainer spacing={3}>
          <GridItem sm={6} md={6}>
            <InputFormField required={true} fieldName={"name"} labelCode={"l.globalMessage.name"} />
          </GridItem>
          <GridItem sm={6} md={6}>
            <InputFormField required={true} fieldName={"nameEn"} labelCode={"l.globalMessage.nameEn"} />
          </GridItem>
          <GridItem sm={6} md={6}>
            <SelectFormField
              addEmptyOption={false}
              selectOptions={globalMessageTypeMap}
              required={true}
              fieldName={"type.id"}
              labelCode={"l.globalMessage.type"}
            />
          </GridItem>
          <GridItem sm={6} md={6}>
            <SelectFormField
              addEmptyOption={false}
              selectOptions={[
                { value: true, text: t("l.yes") },
                { value: false, text: t("l.no") },
              ]}
              required={true}
              fieldName={"active"}
              labelCode={"l.globalMessage.active"}
            />
          </GridItem>
          <GridItem sm={12} md={12}>
            <TextareaFormField required={true} fieldName={"text"} labelCode={"l.globalMessage.text"} />
          </GridItem>
          <GridItem sm={12} md={12}>
            <TextareaFormField required={true} fieldName={"textEn"} labelCode={"l.globalMessage.textEn"} />
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

const SaveGlobalMessageDialog = ({ filterFn }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.globalMessageControl.modals.edit;
  });

  const handleCloseDialog = () => {
    dispatch(closeModal({ modalType: "edit" }));
  };

  const { methods } = useReactHookForm<GlobalMessageSaveDetails>({
    defaultValues: globalMessageSaveInitialValues,
    validationSchema: createGlobalMessageSaveValidationSchema,
  });

  useEffect(() => {
    if (open) {
      if (isNotEmpty(id)) {
        const asyncCallArgs: AsyncCallArgs = {
          promise: getGlobalMessage(id),
          onSuccess: (response) => {
            methods.reset(response);
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      } else {
        methods.reset(globalMessageSaveInitialValues);
        setLoading(false);
      }
    }
    // eslint-disable-next-line
    }, [id, open]);

  const onSubmit = (values) => {
    const asyncCreation: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: id === "" ? createGlobalMessage(values) : updateGlobalMessage(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        methods.reset(globalMessageSaveInitialValues, {
          keepIsSubmitted: true,
          keepSubmitCount: true,
        });
        toast.success(t("m.create.globalMessage.success"));
        handleCloseDialog();
        filterFn();
      },
    };
    asyncCall(asyncCreation);
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={id === "" ? "t.globalMessage.create.dialog" : id && "t.globalMessage.edit.dialog"}
      disableEnforceFocus
      dialogActionsSpacing={{ pr: 3 }}
      onSubmitBtnClick={
        !loading && !error && methods.handleSubmit(onSubmit, () => handleSubmitValidationFail(methods.formState, t))
      }
    >
      <Content methods={methods} loading={loading} error={error} />
    </ViewDialog>
  );
};

export default SaveGlobalMessageDialog;
